import type {
  ClerkUser,
  PageAdminUsersFilter,
} from '@agentselly/immodossier-shared';
import {
  ActionSchema,
  AppAction,
  AppRoute,
  pageAdminUsersFilterSchema,
  QueryReference,
} from '@agentselly/immodossier-shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useState } from 'react';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { fetchBackend } from '~/helper/fetchBackend.ts';
import { useAppParams } from '~/helper/useAppParams';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { LegacyUsersPage } from '~/components/admin/LegacyUsersPage';
import { V3Dialog } from '~/components/admin/V3Dialog';
import {
  V3EmptyBlock,
  V3ErrorBlock,
  V3Skeleton,
  V3UsersIcon,
} from '~/components/admin/V3EmptyBlock';

export const Route = createFileRoute(AppRoute.$LANG_$COMPANYID_ADMIN_USERS)({
  component: Body,
});

function Body() {
  const isV3 = useV3Redesign();
  const search = Route.useSearch();

  if (!isV3) {
    return (
      <LegacyUsersPage
        search={search}
        fullPath={Route.fullPath}
      />
    );
  }

  return <V3Users />;
}

const ROLES: Array<[string, string]> = [
  [
    'user',
    'Benutzer',
  ],
  [
    'partner',
    'Partner',
  ],
  [
    'internal',
    'Intern',
  ],
  [
    'admin',
    'Admin',
  ],
];

const ORDERS: Array<[string, string]> = [
  [
    'last_active_at',
    'Zuletzt aktiv',
  ],
  [
    'last_sign_in_at',
    'Letzte Anmeldung',
  ],
  [
    'created_at',
    'Erstellt',
  ],
  [
    'email_address',
    'E-Mail',
  ],
  [
    'first_name',
    'Vorname',
  ],
  [
    'last_name',
    'Nachname',
  ],
];

const date = (ms?: number | null) =>
  ms
    ? new Date(ms).toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '–';

const primaryEmailOf = (user: ClerkUser) =>
  user.emailAddresses?.find((e) => e.id === user.primaryEmailAddressId)
    ?.emailAddress ||
  user.emailAddresses?.[0]?.emailAddress ||
  '';

const nameOf = (user: ClerkUser) =>
  [
    user.firstName,
    user.lastName,
  ]
    .filter(Boolean)
    .join(' ') ||
  primaryEmailOf(user) ||
  user.id;

const statusOf = (user: ClerkUser): [string, string] =>
  user.banned
    ? [
        'Gesperrt',
        'c-bad',
      ]
    : user.locked
    ? [
        'Temporär gesperrt',
        'c-warn',
      ]
    : [
        'Aktiv',
        'c-ok',
      ];

function V3Users() {
  const navigate = useNavigate();
  const { lang, companyId } = useAppParams();
  const parsedSearch = pageAdminUsersFilterSchema.safeParse(Route.useSearch());
  const currentFilter: PageAdminUsersFilter = parsedSearch.success
    ? parsedSearch.data
    : {};
  const query = useBackendQuery(QueryReference.PageAdminUsers);
  const [
    selectedUserId,
    setSelectedUserId,
  ] = useState('');

  async function updateFilter(filter: PageAdminUsersFilter) {
    const newFilter = { ...currentFilter, ...filter };
    if (!('page' in filter)) {
      newFilter.page = 1;
    }
    const nextSearch: Record<string, unknown> = {};
    for (const key of Object.keys(
      pageAdminUsersFilterSchema._def.shape(),
    ) as (keyof PageAdminUsersFilter)[]) {
      const value = newFilter[key];
      if (
        typeof value === 'undefined' ||
        value === null ||
        value === '' ||
        value === false ||
        (key === 'page' && value === 1)
      ) {
        continue;
      }
      nextSearch[key] = value;
    }
    await navigate({
      to: Route.fullPath,
      params: { lang, companyId },
      search: nextSearch as unknown as PageAdminUsersFilter,
      replace: true,
    });
    await query.refetch();
  }

  const head = (
    <div
      className="v3-head"
      data-od-id="page-head"
    >
      <h1>Benutzer</h1>
      <p className="v3-mu">Clerk-Benutzer suchen und verwalten.</p>
    </div>
  );

  if (query.isLoading) {
    return (
      <div
        className="v3-stack pb-8"
        aria-busy="true"
      >
        {head}
        <V3Skeleton />
      </div>
    );
  }

  if (query.isError || !parsedSearch.success) {
    return (
      <div className="v3-stack pb-8">
        {head}
        <V3ErrorBlock
          title="Benutzer konnten nicht geladen werden"
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const users = (query.data?.users ?? []).filter(Boolean);
  const pagination = query.data?.pagination;
  const page = pagination?.page || 1;
  const pageCount = pagination?.pageCount || 1;
  const selectedUser = users.find((u) => u.id === selectedUserId);
  const commitQuery = (value: string) => {
    if (value !== (currentFilter.query ?? '')) {
      void updateFilter({ query: value });
    }
  };

  return (
    <div className="v3-stack pb-8">
      {head}

      <section
        className="v3-card"
        data-od-id="filters"
        aria-label="Filter"
      >
        <div className="v3-fil">
          <label className="f">
            Suche
            <span
              className="v3-search"
              style={{ maxWidth: 'none' }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                type="search"
                aria-label="Suche"
                placeholder="Name, E-Mail, Benutzername"
                defaultValue={currentFilter.query ?? ''}
                onBlur={(e) => commitQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitQuery(e.currentTarget.value);
                  }
                }}
              />
            </span>
          </label>
          <label className="f">
            Sortieren nach
            <select
              className="v3-sel"
              value={currentFilter.orderBy || 'last_active_at'}
              onChange={(e) =>
                updateFilter({
                  orderBy: e.target.value as PageAdminUsersFilter['orderBy'],
                })
              }
            >
              {ORDERS.map(
                ([
                  value,
                  label,
                ]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {label}
                  </option>
                ),
              )}
            </select>
          </label>
          <div>
            <label
              className="v3-ckb"
              htmlFor="filterOrderByAscending"
            >
              <input
                id="filterOrderByAscending"
                type="checkbox"
                checked={Boolean(currentFilter.orderByAscending)}
                onChange={(e) =>
                  updateFilter({ orderByAscending: e.target.checked })
                }
              />
              <span>Aufsteigend</span>
            </label>
          </div>
        </div>
      </section>

      {users.length ? (
        <div
          className="v3-stack"
          data-od-id="user-list"
        >
          {users.map((user) => {
            const [
              statusLabel,
              statusClass,
            ] = statusOf(user);
            const role = String(user.publicMetadata?.role || 'user');
            const email = primaryEmailOf(user);
            return (
              <article
                key={user.id}
                className="v3-card flex"
                style={{ alignItems: 'flex-start', gap: 12 }}
                data-od-id={`user-${user.id.slice(-4)}`}
              >
                <span
                  className="v3-av4"
                  aria-hidden="true"
                >
                  {(user.firstName || email || 'U').slice(0, 1).toUpperCase()}
                </span>
                <div
                  className="v3-stack"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <div
                    className="flex flex-wrap justify-between"
                    style={{ alignItems: 'flex-start', gap: 8 }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {nameOf(user)}
                      </p>
                      <p
                        className="v3-sm v3-mu"
                        style={{ overflowWrap: 'anywhere' }}
                      >
                        {email}
                      </p>
                    </div>
                    <span
                      className="flex flex-wrap"
                      style={{ gap: 6 }}
                    >
                      <span className={clsx('v3-chip', statusClass)}>
                        <i />
                        {statusLabel}
                      </span>
                      <span className="v3-chip c-neu">
                        <i />
                        {ROLES.find((r) => r[0] === role)?.[1] ?? role}
                      </span>
                    </span>
                  </div>
                  <dl className="v3-kv">
                    <div>
                      <dt>Zuletzt aktiv</dt>
                      <dd className="v3-num">{date(user.lastActiveAt)}</dd>
                    </div>
                    <div>
                      <dt>Letzte Anmeldung</dt>
                      <dd className="v3-num">{date(user.lastSignInAt)}</dd>
                    </div>
                    <div>
                      <dt>Erstellt</dt>
                      <dd className="v3-num">{date(user.createdAt)}</dd>
                    </div>
                    <div>
                      <dt>Benutzername</dt>
                      <dd>{user.username || '–'}</dd>
                    </div>
                  </dl>
                  <div className="v3-lk">
                    <button
                      type="button"
                      className="v3-btn sm"
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      Details
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <V3EmptyBlock
          icon={V3UsersIcon}
          title="Keine Benutzer gefunden"
          text="Zu dieser Suche gibt es keinen Treffer. Anderen Namen oder eine andere E-Mail probieren."
          actions={
            <button
              type="button"
              className="v3-btn"
              onClick={() => void updateFilter({ query: '' })}
            >
              Suche zurücksetzen
            </button>
          }
        />
      )}

      {users.length ? (
        <nav
          className="v3-pg"
          aria-label="Seiten"
          data-od-id="pagination"
        >
          <span className="v3-xs v3-mu v3-num">
            {pagination?.total ?? users.length} Benutzer
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="v3-btn"
              disabled={page <= 1}
              onClick={() => updateFilter({ page: page - 1 })}
            >
              Zurück
            </button>
            <button
              type="button"
              className="v3-btn"
              disabled={page >= pageCount}
              onClick={() => updateFilter({ page: page + 1 })}
            >
              Weiter
            </button>
          </span>
        </nav>
      ) : null}

      {selectedUser ? (
        <V3Dialog
          id={`u-${selectedUser.id.slice(-4)}`}
          title={nameOf(selectedUser)}
          onClose={() => setSelectedUserId('')}
        >
          <UserDetail
            user={selectedUser}
            canMutate={Boolean(query.data?.canMutate)}
          />
        </V3Dialog>
      ) : null}
    </div>
  );
}

function UserDetail(props: {
  readonly user: ClerkUser;
  readonly canMutate: boolean;
}) {
  const { user, canMutate } = props;
  const queryClient = useQueryClient();
  const metadata = user.publicMetadata || {};
  const initialRole = String(metadata.role || 'user');
  const hubspotContactId = metadata.hubspotContactId
    ? String(metadata.hubspotContactId)
    : '';
  const [
    role,
    setRole,
  ] = useState(initialRole);
  const [
    confirming,
    setConfirming,
  ] = useState(false);
  const [
    busy,
    setBusy,
  ] = useState(false);
  const [
    message,
    setMessage,
  ] = useState<{ ok: boolean; text: string } | null>(null);

  async function save(next: { role: string; banned: boolean }) {
    setBusy(true);
    setMessage(null);
    try {
      const body = ActionSchema[AppAction.UserMutation].safeParse({
        userId: user.id,
        role: next.role,
        hubspotContactId: hubspotContactId
          ? parseInt(hubspotContactId, 10)
          : undefined,
        banned: next.banned,
        locked: Boolean(metadata.locked),
      });
      if (!body.success) {
        throw body.error;
      }
      const response = await fetchBackend(`/action/${AppAction.UserMutation}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body.data),
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const result = await response.json();
      if (!result.success) {
        throw new Error(JSON.stringify(result.errors));
      }
      setMessage({ ok: true, text: 'Benutzer erfolgreich angepasst.' });
      await queryClient.invalidateQueries({
        queryKey: [QueryReference.PageAdminUsers],
      });
    } catch (err) {
      setMessage({
        ok: false,
        text: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  const banned = Boolean(user.banned);

  return (
    <>
      <dl className="v3-kv">
        <div>
          <dt>E-Mail</dt>
          <dd>
            {primaryEmailOf(user) || '–'}{' '}
            {primaryEmailOf(user) ? (
              <span className="v3-chip c-info">primär</span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Telefon</dt>
          <dd>{user.phoneNumbers?.[0]?.phoneNumber || '–'}</dd>
        </div>
        <div>
          <dt>Clerk-ID</dt>
          <dd className="v3-num">{user.id}</dd>
        </div>
        <div>
          <dt>HubSpot-Kontakt</dt>
          <dd className="v3-num">{hubspotContactId || '–'}</dd>
        </div>
      </dl>
      <div className="v3-lk">
        <a
          className="v3-btn sm"
          href={`https://dashboard.clerk.com/apps/app_2qxN29A6IozvGlwUx1mNAjiFtH0/instances/ins_2siVGQt4cvjzblObK1CneZ6KatG/users/${user.id}`}
          target="_blank"
          rel="noreferrer"
        >
          In Clerk öffnen
        </a>
        {hubspotContactId ? (
          <a
            className="v3-btn sm"
            href={`https://app-eu1.hubspot.com/contacts/26776524/record/0-1/${hubspotContactId}/`}
            target="_blank"
            rel="noreferrer"
          >
            In HubSpot öffnen
          </a>
        ) : null}
      </div>

      <section
        className="v3-stack"
        style={{ gap: 8 }}
      >
        <h3>Rolle ändern</h3>
        <div className="v3-warnbox">
          <b>Achtung:</b> Die Rolle steuert Zugriff auf Objekte und den
          Admin-Bereich. Änderung wirkt sofort.
        </div>
        <div className="v3-sel2">
          <select
            className="v3-sel"
            aria-label="Rolle"
            value={role}
            disabled={!canMutate || busy}
            onChange={(e) => setRole(e.target.value)}
          >
            {ROLES.map(
              ([
                value,
                label,
              ]) => (
                <option
                  key={value}
                  value={value}
                >
                  {label}
                </option>
              ),
            )}
          </select>
          <button
            type="button"
            className="v3-btn pri"
            disabled={!canMutate || busy || role === initialRole}
            onClick={() => setConfirming(true)}
          >
            Rolle speichern
          </button>
        </div>
      </section>

      <section
        className="v3-stack"
        style={{ gap: 8 }}
      >
        <h3>Konto</h3>
        <div className="v3-lk">
          <button
            type="button"
            className="v3-btn sm"
            disabled={!canMutate || busy}
            onClick={() => void save({ role: initialRole, banned: !banned })}
          >
            {banned ? 'Entsperren' : 'Sperren'}
          </button>
          {/* ponytail: no session-revoke action in the backend yet; add an AppAction and enable. */}
          <button
            type="button"
            className="v3-btn sm"
            disabled
          >
            Sitzungen beenden
          </button>
        </div>
        {!canMutate ? (
          <p className="v3-xs v3-mu">
            Nur lesender Zugriff in dieser Umgebung.
          </p>
        ) : null}
        {message ? (
          <p
            className={clsx('v3-xs', !message.ok && 'text-[var(--v3-bad)]')}
            role={message.ok ? 'status' : 'alert'}
          >
            {message.text}
          </p>
        ) : null}
      </section>

      {confirming ? (
        <V3Dialog
          id="role-confirm"
          title="Rollenänderung bestätigen"
          onClose={() => setConfirming(false)}
        >
          <p>
            Möchten Sie die Rolle für diesen Benutzer wirklich von{' '}
            <strong>{initialRole}</strong> auf <strong>{role}</strong> ändern?
          </p>
          <p className="v3-xs v3-mu">
            Diese Änderung wirkt sich unmittelbar auf Zugriffsrechte und
            Autorisierungen des Nutzers aus.
          </p>
          <div className="v3-lk">
            <button
              type="button"
              className="v3-btn sm"
              onClick={() => setConfirming(false)}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="v3-btn sm pri"
              disabled={busy}
              onClick={() => void save({ role, banned })}
            >
              Bestätigen &amp; Speichern
            </button>
          </div>
        </V3Dialog>
      ) : null}
    </>
  );
}
